import styled from '@emotion/styled';
import {
  type CheckboxProps as MuiCheckboxProps,
  type FormControlLabelProps,
  type FormHelperTextProps,
  type FormControlProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox as MuiCheckbox,
} from '@mui/material';

export type CheckboxProps = MuiCheckboxProps & {
  formControlLabelProps?: FormControlLabelProps;
  formControlProps?: FormControlProps;
  label?: FormControlLabelProps['label'];
  name?: MuiCheckboxProps['name'];
  formHelperTextProps?: FormHelperTextProps;
  error?: boolean;
  helperText?: string | React.ReactNode;
};

/**
 * Checkbox component, part of Delogue design system.
 * @param checked - whether the checkbox is checked
 * @param indeterminate - whether the checkbox is in indeterminate state, when true checked is ignored
 * @param label - label for the checkbox, show on the right side of the checkbox
 * @param error - whether the checkbox is in error state, when true helperText will be shown in red
 * @param helperText - text to be displayed below the checkbox, can be used to show validation errors or additional information
 * @param required - whether the checkbox is required in a form, when true a red asterisk will be shown next to the label
 * @param onChange - callback function to be called when the checkbox is checked or unchecked
 * @param onBlur - callback function to be called when the checkbox loses focus
 * @param disabled - whether the checkbox is disabled, when true the checkbox will be greyed out and not clickable
 */
export const Checkbox = ({
  formControlLabelProps,
  required,
  disabled,
  label,
  formHelperTextProps,
  formControlProps,
  onChange,
  onBlur,
  error,
  name,
  helperText,
  ...props
}: CheckboxProps) => {
  return (
    <FormControl
      required={required}
      error={error}
      disabled={disabled}
      component="fieldset"
      {...formControlProps}
    >
      <StyledFormControlLabel
        {...formControlLabelProps}
        control={
          <StyledCheckbox
            required={required}
            disabled={disabled}
            name={name}
            {...props}
            onChange={(event, checked) => {
              onChange?.(event, checked);
            }}
            onBlur={e => {
              onBlur?.(e);
            }}
          />
        }
        required={required}
        disabled={disabled}
        name={name}
        label={label}
      />
      {(helperText || formHelperTextProps?.children) && (
        <StyledHelperText
          required={required}
          disabled={disabled}
          error={error}
          {...formHelperTextProps}
        >
          {helperText || formHelperTextProps?.children}
        </StyledHelperText>
      )}
    </FormControl>
  );
};

const StyledHelperText = styled(FormHelperText)`
  margin-left: 10px;
  margin-right: 10px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-left: unset;
  margin-right: unset;
`;

export const StyledCheckbox = styled(MuiCheckbox)`
  padding: 8px;
  svg {
    color: ${({ theme }) => theme.palette.fg.brandSecondary};
  }
  &.Mui-disabled svg {
    color: ${({ theme }) => theme.palette.bg.disabled};
  }
  :hover {
    background-color: ${({ theme }) => theme.palette.component.checkbox.hover};
  }
  :focus,
  :active {
    background-color: ${({ theme }) => theme.palette.component.checkbox.focus};
  }
  .MuiTouchRipple-ripple .MuiTouchRipple-child {
    background-color: ${({ theme }) => theme.palette.component.checkbox.ripple};
  }
  &:not(.Mui-checked):not(.MuiCheckbox-indeterminate) {
    svg {
      color: ${({ theme }) => theme.palette.border.primarySubtle};
    }
    &.Mui-disabled svg {
      color: ${({ theme }) => theme.palette.border.disabledSubtle};
    }
    &:hover {
      background-color: ${({ theme }) => theme.palette.component.checkbox.hover2};
    }
    &:focus,
    &:active {
      background-color: ${({ theme }) => theme.palette.component.checkbox.focus2};
    }
    .MuiTouchRipple-ripple .MuiTouchRipple-child {
      background-color: ${({ theme }) => theme.palette.component.checkbox.ripple2};
    }
  }
`;
